import classNames from 'classnames';
import { WeekDay } from 'constants/recurrence';
import { useFormikContext } from 'formik';
import { omit } from 'lodash';
import startCase from 'lodash/startCase';
import { FC, PropsWithChildren, ReactNode, useMemo } from 'react';
import { ReactSVG } from 'react-svg';
import TooltipLite from 'react-tooltip-lite';
import { getMinDate } from 'utils/booking';
import { dateFormatForUI } from 'utils/date';
import moment from 'utils/dayjs';
import { useTranslation } from 'utils/i18n';
import {
  DESKTOP_TOOLTIP_DISTANCE,
  SHARED_TOOLTIP_DISTANCE,
} from '../../constants/breakpoints';
import useIsMobile from '../../hooks/useIsMobile';
import {
  getInitialValues,
  type StoredBooking,
} from '../../hooks/useStoredBooking';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import Checkbox from '../fields/checkbox';
import DatepickerField from '../fields/datepickerField';
// components
import Field from '../fields/field';
import RadioButton from '../fields/radioButton';
import SelectList from '../fields/selectList';
import LoadingButton from './loadingButton';
import RecurrenceStatus from './recurrenceStatus';

// react-tooltip-lite's published types predate React 19 and omit `children`.
const Tooltip = TooltipLite as unknown as FC<
  PropsWithChildren<{
    content: ReactNode;
    className?: string;
    distance?: number;
  }>
>;

const RecurrenceType = ['daily', 'weekly', 'monthly'];

// Re-exported for the components that already import it from here.
export { WeekDay };

const WeekNumber = {
  First: 1,
  Second: 2,
  Third: 3,
  Fourth: 4,
  Last: -1,
};

interface Props {
  fieldKey?: string;
  clashCount?: number;
  updatesEnabled?: boolean;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  onCheck?: (clashesDetected?: boolean) => void;
  maxDate?: Date | null;
}

// The recurrence block of the stored-booking form values.
type StoredRecurrence = StoredBooking['recurrence'];

interface RecurrenceValues {
  date?: Date;
  service?: string | number;
  slots?: Array<{ startTime: string; endTime: string }>;
  // The recurrence block can live under any caller-chosen fieldKey, so the
  // form values stay open beyond the fields read here.
  [key: string]: unknown;
}

const dateDependentFields = ['nthDayOfMonth', 'weekDay', 'weekDays', 'endDate'];

const RecurrenceOptions: FC<Props> = ({
  fieldKey = 'recurrence',
  clashCount = 0,
  updatesEnabled,
  loading = false,
  disabled: disabledProp,
  className: classNameProp = 'recurrence__default',
  onCheck = () => {},
  maxDate = null,
}) => {
  const { t } = useTranslation('common');
  const className = classNames('recurrence', classNameProp);

  const NUM_DAYS = 31;

  const fieldNamePrefix = `${fieldKey}.`;

  const isMobileView = useIsMobile();

  const {
    values: { date, service, slots = [], [fieldKey]: recurrenceValue } = {},
    errors: formikErrors = {},
    setFieldValue,
    validateForm,
  } = useFormikContext<RecurrenceValues>();

  const recurrence = (recurrenceValue ?? {}) as Partial<StoredRecurrence>;

  const { recurrence: errors = {} } = formikErrors as {
    recurrence?: { count?: string; endDate?: string };
  };

  const _checkedJson = useMemo(
    () => JSON.stringify(omit(recurrence, ['checked'])),
    [recurrence]
  );

  // biome-ignore lint: JSON stringification for dependency tracking
  useUpdateEffect(
    () => {
      setFieldValue(`${fieldNamePrefix}checked`, false);
    },
    [setFieldValue, fieldNamePrefix, _checkedJson],
    updatesEnabled
  );

  const { type: recurrenceType } = recurrence;

  useUpdateEffect(
    () => {
      const { recurrence: fields = {} } = getInitialValues({
        recurrenceRules: { type: recurrenceType },
      });

      const excludedFields = [
        'type',
        'enabled',
        'checked',
        ...dateDependentFields,
      ];

      const fieldNames = Object.keys(fields).filter(
        field => !excludedFields.includes(field)
      );

      fieldNames.forEach(fieldName => {
        setFieldValue(
          `${fieldNamePrefix}${fieldName}`,
          (fields as Record<string, unknown>)[fieldName]
        );
      });
    },
    [setFieldValue, fieldNamePrefix, recurrenceType],
    updatesEnabled
  );

  useUpdateEffect(
    () => {
      const { recurrence: fields = {} } = getInitialValues({
        startTime: date,
        recurrenceRules: { type: recurrenceType },
      });

      const fieldNames = dateDependentFields.filter(
        field => field !== 'endDate'
      );

      const setFields = async () => {
        await Promise.all(
          fieldNames.map(fieldName =>
            setFieldValue(
              `${fieldNamePrefix}${fieldName}`,
              (fields as Record<string, unknown>)[fieldName]
            )
          )
        );
        await validateForm();
      };

      setFields();
    },
    [setFieldValue, validateForm, fieldNamePrefix, date, recurrenceType],
    updatesEnabled
  );

  const minDate = useMemo(
    () => getMinDate(date!, recurrence),
    [date, recurrence]
  );

  const minDateString = useMemo(
    () => (minDate ? String(minDate) : undefined),
    [minDate]
  );

  useUpdateEffect(
    () => {
      const setFields = async () => {
        await setFieldValue(
          `${fieldNamePrefix}endDate`,
          new Date(minDateString!)
        );
        await validateForm();
      };

      if (minDateString) setFields();
    },
    [setFieldValue, validateForm, fieldNamePrefix, minDateString],
    updatesEnabled
  );

  const {
    enabled: recurrenceEnabled,
    checked: recurrenceChecked,
    everyNumberOf,
    weekDays = [],
    dayRange,
    dayOfMonthRange,
    rangeEndType,
  } = recurrence;

  useUpdateEffect(
    () => {
      if (!recurrenceEnabled) {
        const { recurrence: fields } = getInitialValues({ startTime: date });
        Object.keys(fields).forEach(fieldName => {
          setFieldValue(
            `${fieldNamePrefix}${fieldName}`,
            (fields as Record<string, unknown>)[fieldName]
          );
        });
      }
    },
    [setFieldValue, fieldNamePrefix, recurrenceEnabled, date],
    updatesEnabled
  );

  const disabled =
    disabledProp !== undefined
      ? disabledProp
      : !date || !service || slots.length === 0;

  const numericOptions = (count: number) => {
    return Array(count)
      .fill(null)
      .map((_, day) => ({
        label: (day + 1).toString(),
        value: day + 1,
      }));
  };

  const recurrenceTypeOptions = () => {
    return RecurrenceType.map(key => {
      return {
        label: startCase(key),
        value: key,
      };
    });
  };

  const weeklyOptions = () => {
    return WeekDay.map(key => {
      return {
        label: startCase(key).slice(0, 3),
        value: key,
      };
    });
  };

  const monthlyOptions = () => {
    return Object.keys(WeekNumber).map(key => {
      return {
        label: key,
        value: WeekNumber[key as keyof typeof WeekNumber],
      };
    });
  };

  const maxOccurrences = {
    daily: Math.ceil(
      moment(maxDate).diff(moment(minDate), 'days', true) *
        (dayRange === 'weekdays' ? 5 / 7 : 1)
    ),
    weekly: Math.ceil(moment(maxDate).diff(moment(minDate), 'weeks', true)),
    monthly: Math.ceil(moment(maxDate).diff(moment(minDate), 'months', true)),
  };

  const clashesDetected = !!recurrenceChecked && clashCount > 0;

  const buttonText = clashesDetected
    ? t('quick_book.recurrence.fix_clashes')
    : t('quick_book.recurrence.check_and_reserve');

  const renderDailyOptions = () => (
    <>
      <div className="recurrence__option recurrence__option--frequency-range">
        <Field
          name="dayRange"
          namePrefix={fieldNamePrefix}
          as={RadioButton}
          type="radio"
          id="recurrenceDays"
          value="days"
          label={t('quick_book.recurrence.every')}
        />
        <Field
          name="everyNumberOf"
          namePrefix={fieldNamePrefix}
          as={SelectList}
          setValueProp="onChange"
          label={t('quick_book.recurrence.recur_every')}
          hideLabel
          options={numericOptions(NUM_DAYS)}
          disabled={dayRange !== 'days'}
          data-testid="everyNumberOf"
        />
        <span>{t('quick_book.recurrence.days')}</span>
      </div>
      <div className="recurrence__option recurrence__option--every-weekday">
        <Field
          name="dayRange"
          namePrefix={fieldNamePrefix}
          as={RadioButton}
          type="radio"
          id="recurrenceWeekdays"
          value="weekdays"
          label={t('quick_book.recurrence.every_weekday')}
        />
      </div>
    </>
  );

  const renderWeeklyOptions = () => (
    <>
      <div className="recurrence__option recurrence__option--recur-every">
        <Field
          name="everyNumberOf"
          namePrefix={fieldNamePrefix}
          as={SelectList}
          setValueProp="onChange"
          label={t('quick_book.recurrence.recur_every')}
          hideLabel={false}
          options={numericOptions(20)}
        />
        <span>weeks on</span>
      </div>
      <div className="recurrence__option recurrence__option--selected-weekday">
        {WeekDay.map(day => {
          return (
            <div className="recurrence__option__day" key={day}>
              <Field
                name="weekDays"
                namePrefix={fieldNamePrefix}
                as={Checkbox}
                type="checkbox"
                id={day}
                label={day.slice(0, 3)}
                value={day}
              />
            </div>
          );
        })}
      </div>
    </>
  );

  const renderMonthlyOptions = () => (
    <>
      <div className="recurrence__option recurrence__option--frequency-range">
        <Field
          name="dayOfMonthRange"
          namePrefix={fieldNamePrefix}
          as={RadioButton}
          type="radio"
          id="recurrence_day_of_month"
          value="nthDayOfMonth"
          label={t('quick_book.recurrence.day')}
          data-testid="nthDayOfMonth"
        />
        <Field
          name="nthDayOfMonth"
          namePrefix={fieldNamePrefix}
          as={SelectList}
          setValueProp="onChange"
          hideLabel
          disabled={dayOfMonthRange !== 'nthDayOfMonth'}
          options={numericOptions(31)}
          testid="dayNumber"
        />
        <span>
          each month
          <Tooltip
            content={t('quick_book.recurrence.monthly_days_help_text')}
            className="recurrence__tooltip"
            distance={
              isMobileView ? SHARED_TOOLTIP_DISTANCE : DESKTOP_TOOLTIP_DISTANCE
            }
          >
            <ReactSVG
              src="/images/icons/question-mark.svg"
              width={17}
              height={17}
            />
          </Tooltip>
        </span>
      </div>
      <div className="recurrence__option recurrence__option--frequency-range">
        <Field
          name="dayOfMonthRange"
          namePrefix={fieldNamePrefix}
          as={RadioButton}
          type="radio"
          id="recurrence_day_of_week_of_month"
          value="nthWeekdayOfMonth"
          label={t('quick_book.recurrence.every')}
          data-testid="nthWeekdayOfMonth"
        />
        <Field
          name="weekNumber"
          namePrefix={fieldNamePrefix}
          as={SelectList}
          setValueProp="onChange"
          hideLabel
          options={monthlyOptions()}
          disabled={dayOfMonthRange !== 'nthWeekdayOfMonth'}
          testid="weekNumber"
        />
        <Field
          name="weekDay"
          namePrefix={fieldNamePrefix}
          as={SelectList}
          setValueProp="onChange"
          hideLabel
          options={weeklyOptions()}
          disabled={dayOfMonthRange !== 'nthWeekdayOfMonth'}
          testid="weekDay"
        />
      </div>
    </>
  );

  const renderOptions = () => {
    switch (recurrenceType) {
      case 'daily': {
        return renderDailyOptions();
      }

      case 'weekly': {
        return renderWeeklyOptions();
      }

      case 'monthly': {
        return renderMonthlyOptions();
      }

      default: {
        return null;
      }
    }
  };

  const renderRangeOfRecurrence = () => (
    <>
      <h4 className="recurrence__text">{t('quick_book.recurrence.text')}</h4>

      <div className="recurrence__option recurrence__option--end-by">
        <Field
          name="rangeEndType"
          namePrefix={fieldNamePrefix}
          as={RadioButton}
          type="radio"
          id="recurrenceRangeEndDate"
          value="date"
          label={t('quick_book.recurrence.end_by')}
        />
        <Field
          name="endDate"
          namePrefix={fieldNamePrefix}
          as={DatepickerField}
          setValueProp="onChange"
          dateFormat={dateFormatForUI}
          minDate={minDate}
          maxDate={new Date(maxDate!)}
          disabled={rangeEndType !== 'date'}
        />
      </div>
      <div className="recurrence__option recurrence__option--end-after">
        <Field
          name="rangeEndType"
          namePrefix={fieldNamePrefix}
          as={RadioButton}
          type="radio"
          id="recurrenceRangeEndCount"
          value="count"
          label={t('quick_book.recurrence.end_after')}
        />
        <div className="field__input">
          <Field
            name="count"
            namePrefix={fieldNamePrefix}
            type="number"
            min="2"
            max={
              maxOccurrences[
                String(recurrenceType) as keyof typeof maxOccurrences
              ]
            }
            step="1"
            disabled={rangeEndType !== 'count'}
            data-testid="recurrenceCount"
          />
        </div>
        <span>occurrences</span>

        {(errors.count || errors.endDate) && (
          <div
            className="recurrence__max_occurences_error"
            data-testid="maxOccurrencesError"
            data-cy="maxOccurrencesError"
          >
            <i className="fas fa-exclamation-triangle space__icon" />
            {errors.count || errors.endDate}
          </div>
        )}
      </div>
    </>
  );

  const requiredOptionsSelected = () => {
    if (errors.count || errors.endDate) {
      return false;
    }

    switch (recurrenceType) {
      case 'daily': {
        return !!everyNumberOf || dayRange === 'weekdays';
      }

      case 'weekly': {
        return weekDays.length;
      }

      case 'monthly': {
        return true;
      }

      default: {
        return false;
      }
    }
  };

  const renderEnabledCheckbox = () => (
    <Field
      name="enabled"
      namePrefix={fieldNamePrefix}
      as={Checkbox}
      type="checkbox"
      label={t('quick_book.recurrence.set_booking_as_recurring')}
      disabled={disabled}
      data-testid="toggleRecurrence"
    />
  );

  if (!recurrenceEnabled || disabledProp)
    return <div className={className}>{renderEnabledCheckbox()}</div>;

  const handleClick = () => {
    if (!clashesDetected) setFieldValue(`${fieldNamePrefix}checked`, true);

    onCheck(clashesDetected);
  };

  return (
    <div className={className} id="recurrence__quickbook">
      {renderEnabledCheckbox()}
      <div className="recurrence__container" data-testid="recurrenceContainer">
        <div className="recurrence__row">
          <div>
            <div className="recurrence__option recurrence__option--frequency">
              <Field
                name="type"
                namePrefix={fieldNamePrefix}
                as={SelectList}
                setValueProp="onChange"
                label="Recurrence"
                hideLabel={false}
                options={recurrenceTypeOptions()}
              />
            </div>
            {renderOptions()}
          </div>
          <div>{renderRangeOfRecurrence()}</div>
        </div>

        <div>
          {!errors.count && !errors.endDate && (
            <RecurrenceStatus
              statusChecked={!!recurrenceChecked}
              clashCount={clashCount}
            />
          )}

          <LoadingButton
            text={buttonText}
            loading={loading}
            disabled={loading || !requiredOptionsSelected()}
            hasError={clashesDetected}
            handleClick={handleClick}
          />
        </div>
      </div>
    </div>
  );
};

export default RecurrenceOptions;
